import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Bot, LineChart, CheckCircle2 } from "lucide-react";

const ONBOARDING_KEY = "clinisharp_has_seen_welcome_tour";

const steps = [
  {
    icon: <Sparkles className="w-12 h-12 text-teal-500 mb-4" />,
    title: "Bem-vindo ao novo CliniSharp",
    description: "Reimaginamos completamente a experiência para sua clínica. O novo design é mais rápido, limpo e construído para maximizar a produtividade da sua equipe. Prepare-se para um salto operacional.",
    gradient: "from-teal-500 to-emerald-500",
  },
  {
    icon: <Bot className="w-12 h-12 text-indigo-500 mb-4" />,
    title: "Sua Clínica no Piloto Automático",
    description: "Sua nova assistente (IA Copilot) agora trabalha 24h por dia. Ela prospecta, responde pacientes no Omnichannel e marca consultas fora do horário comercial enquanto você dorme, dobrando seus fechamentos.",
    gradient: "from-indigo-500 to-purple-500",
  },
  {
    icon: <LineChart className="w-12 h-12 text-blue-500 mb-4" />,
    title: "Vend+ — sua equipe comercial 24/7",
    description: "Acompanhe todo o pipeline no novo CRM de Oportunidades e descubra o ROI exato (Retorno sobre Investimento) da sua IA diretamente no seu Dashboard em tempo real. Não perca nenhum lead.",
    gradient: "from-blue-500 to-teal-500",
  }
];

export function WelcomeModal() {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Check if the user has already seen the onboarding
    const hasSeen = localStorage.getItem(ONBOARDING_KEY);
    if (!hasSeen) {
      // Delay opening slightly to let the app load first
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setOpen(false);
  };

  const handleNext = () => {
    // Functional update prevents race condition when user double/triple-clicks
    // "Próximo" before React commits the previous render. Without this, stale
    // closure on currentStep can push it past steps.length-1 and crash on undefined.
    setCurrentStep(prev => {
      if (prev < steps.length - 1) return prev + 1;
      // Already on last step: persist + close (deferred to avoid setState during render).
      Promise.resolve().then(handleComplete);
      return prev;
    });
  };

  // Defensive: even if currentStep ever exceeds bounds, fall back to last step
  // so the gradient/icon/title accessors never read from undefined.
  const step = steps[currentStep] ?? steps[steps.length - 1];

  return (
    <Dialog open={open} onOpenChange={(val) => {
      if (!val) handleComplete(); // Close gracefully
    }}>
      <DialogContent className="sm:max-w-[425px] md:max-w-[500px] p-0 border-none bg-white rounded-3xl overflow-hidden shadow-2xl">
        <DialogTitle className="sr-only">Boas-vindas ao CliniSharp</DialogTitle>
        <DialogDescription className="sr-only">Tour interativo apresentando as novas funcionalidades</DialogDescription>
        
        {/* Animated Background Banner */}
        <div className={`h-40 w-full bg-gradient-to-r ${step.gradient} relative overflow-hidden transition-all duration-700`}>
           <div className="absolute inset-0 bg-white/10 backdrop-blur-3xl" />
           {/* Decorative elements */}
           <div className="absolute top-10 right-10 w-32 h-32 bg-white/20 rounded-full blur-2xl" />
           <div className="absolute -bottom-10 left-10 w-24 h-24 bg-black/10 rounded-full blur-xl" />
        </div>

        {/* Content Area */}
        <div className="px-8 pt-6 pb-8 relative bg-white -mt-8 rounded-t-3xl h-[280px] flex flex-col justify-between">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center text-center"
            >
              <div className="bg-white p-3 rounded-2xl shadow-xl border border-slate-100 -mt-20 mb-4">
                 {step.icon}
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-3">{step.title}</h2>
              <p className="text-sm text-slate-500 leading-relaxed font-medium">
                {step.description}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="pt-6 mt-auto">
            <div className="flex justify-between items-center w-full">
              {/* Dots Indicator */}
              <div className="flex gap-2">
                {steps.map((_, i) => (
                  <div 
                    key={i} 
                    className={`h-2 rounded-full transition-all duration-500 ${i === currentStep ? 'w-6 bg-teal-500' : 'w-2 bg-slate-200'}`}
                  />
                ))}
              </div>
              
              <Button 
                onClick={handleNext} 
                size="lg"
                className="rounded-full shadow-lg hover:shadow-xl transition-all px-8 bg-slate-900 hover:bg-slate-800 text-white"
              >
                {currentStep === steps.length - 1 ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Começar
                  </>
                ) : (
                  'Próximo'
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
